import { createPortal } from "react-dom";

// ─── MODAL PORTAL ─────────────────────────────────────────────────────────────
// Renders children directly into document.body, escaping all parent containers.
// This fixes the pink-tinted background and cut-off panel issues entirely.
// zIndex defaults to 9000, which sits above the app shell. Slide-in sheets
// that render their own portal higher than that (e.g. Habits ▸ Manage Habits
// at 9100) have to pass a higher value for any modal opened FROM inside them,
// or the modal renders behind the sheet that opened it.
// `warm`: render invisibly and inert (PanelWarmup uses it to lay a panel out
// once before its first real open).
// `animate={false}`: no pop-in / backdrop fade (Jade wants the edit panels to just appear).
function Modal({ children, onClose, blur = true, dim = 0.38, zIndex = 9000, warm = false, animate = true }) {
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
        fontFamily: "'Quicksand', 'Nunito', sans-serif",
        // Own compositing context so scrolling the panel inside doesn't force
        // whatever heavy content sits behind the overlay (e.g. the week grid)
        // to repaint on every scroll frame.
        isolation: "isolate",
        ...(warm ? { opacity: 0, pointerEvents: "none", zIndex: -1, animation: "none" } : {}),
      }}
      {...(warm ? { "aria-hidden": "true", inert: "" } : {})}
    >
      {/* The tint + blur are their own layer that fades in by opacity (Jade,
          2026-10-21). They used to sit on the overlay itself, which only faded
          its colour: the blur snapped on at full strength in the first frame,
          a visible jump before the panel popped (habit details panel). */}
      <div className={animate ? "clarity-fade" : undefined} style={{
        position: "absolute", inset: 0,
        background: `rgba(60,30,50,${dim})`,
        ...(blur ? { backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" } : {}),
      }} />
      {/* clarity-pop: the pop-in every centered panel gets (keyframes in App.jsx).
          clarity-modal-body: dark mode gives the panel inside a shadow (darkModeCss). */}
      <div className={animate ? "clarity-pop clarity-modal-body" : "clarity-modal-body"} onClick={e => e.stopPropagation()} style={{ position: "relative", maxHeight: "90vh", overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", width: "100%", display: "flex", justifyContent: "center", transform: "translateZ(0)", willChange: "transform" }}>
        {children}
      </div>
    </div>,
    document.body
  );
}

export default Modal;
